import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { useSharedSocket } from '@/features/calls/context/CallContext';
import { useAuth } from '@/features/auth/hooks/useAuth';

interface SelfAvailabilityPayload {
  onlineStatus: 'online' | 'busy' | 'offline' | 'paused';
  lastSeenAt?: string;
}

/**
 * Listens for the server's confirmation that the professional's own
 * availability changed and shows a small status toast. Skips the very first
 * event so we don't toast on initial socket connect/snapshot.
 */
export function useSelfAvailabilityToast(): void {
  const socket = useSharedSocket();
  const { user } = useAuth();
  const firstEventRef = useRef(true);

  useEffect(() => {
    if (!socket || user?.role !== 'professional') return;

    const handler = (data: SelfAvailabilityPayload): void => {
      if (firstEventRef.current) {
        firstEventRef.current = false;
        return;
      }
      if (data.onlineStatus === 'online') {
        toast.success('Ora sei online', {
          description: 'I clienti possono contattarti.',
          duration: 4000,
        });
      } else if (data.onlineStatus === 'paused') {
        toast('Sei in pausa: i clienti non possono contattarti', {
          description: 'Riprendi quando vuoi dal menu account o dal profilo.',
          duration: 4000,
        });
      } else if (data.onlineStatus === 'offline') {
        toast('Ora sei offline', {
          description: 'Non sei più visibile come disponibile.',
          duration: 4000,
        });
      }
    };

    socket.on('availability:self', handler);
    return () => {
      socket.off('availability:self', handler);
    };
  }, [socket, user]);
}
